import { useEffect, useMemo, useState } from "react";
import {
  Camera,
  List,
  ListChecks,
  Mail,
  MessageSquare,
  Phone,
  User,
} from "lucide-react";

import { AdminModal } from "@/components/admin/shared/AdminModal";
import { CancelButton, SaveButton } from "@/components/admin/shared/PageHeader";
import { PennButton, PennText, PennTextarea } from "@/components/ui";
import { useAppSelector } from "@/stores/hooks";
import {
  useAddTaskComment,
  useAdminTaskDetail,
  useFinalizeQuote,
  useRespondAndCloseTask,
} from "@/hooks/useTasks";
import { useApprovalState, useDecideApproval } from "@/hooks/useApprovals";
import { useDownloadSupportResponse } from "@/hooks/useQuoteDocuments";
import type { ApprovalState } from "@/api/approvals";
import type {
  AdminTaskDetailManualLineItem,
  AdminTaskDetailSelectedOption,
} from "@/api/adminTasks";

type Props = {
  taskId: string;
  onClose: () => void;
};

const PLACEHOLDER = "—";

// =============================================================================
// Edit-Task modal — reads from `GET /v1/admin/tasks/:taskId` (svc-04). One
// query call, every section. No fan-out into snapshot/state-history/approval/
// manual-line-items hooks. The view-only TaskDetailModal still uses those —
// don't conflate.
// =============================================================================

function fmtUtc(iso: string | null | undefined): string {
  if (!iso) return PLACEHOLDER;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return PLACEHOLDER;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`;
}

function fmtUtcShort(iso: string | null | undefined): string {
  if (!iso) return PLACEHOLDER;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return PLACEHOLDER;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

// Renders an ISO instant in the viewer's LOCAL timezone (used for the approval
// "Done at", which admins read against their own wall clock rather than UTC).
function fmtLocalShort(iso: string | null | undefined): string {
  if (!iso) return PLACEHOLDER;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return PLACEHOLDER;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function initials(
  name: string | null | undefined,
  email: string | null | undefined,
): string {
  const src = (name && name.trim()) || (email && email.trim()) || "?";
  const parts = src.split(/[\s.@_-]+/).filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts[1]?.[0] ?? "";
  return (a + b).toUpperCase();
}

const AVATAR_TONES = [
  "bg-penn-100 text-penn-700 dark:bg-penn-900/40 dark:text-penn-400",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
];
function toneFor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_TONES[Math.abs(h) % AVATAR_TONES.length];
}

function apiErrorMessage(e: unknown, fallback: string): string {
  return (
    (e as { response?: { data?: { error?: { message?: string } } } })?.response
      ?.data?.error?.message ?? fallback
  );
}

function formatPriceFromNumber(
  price: number | null,
  currency: string | null,
): string {
  if (price === null || !Number.isFinite(price)) return PLACEHOLDER;
  const sym =
    currency === "EUR" ? "€" : currency === "USD" ? "$" : `${currency ?? ""} `;
  return `${sym}${price.toFixed(2)}`;
}

function formatNumberWithThousands(n: number | null): string {
  if (n === null) return PLACEHOLDER;
  return new Intl.NumberFormat("en-US").format(n);
}

// Per-step view over the svc-07 approval flow. A step is "decided" once its
// assignment carries a terminal decision; "done by / at" are resolved from the
// matching decision event in the audit trail (actor + timestamp).
type StepView = {
  key: string;
  stepOrder: number;
  stepName: string;
  decision: "approve" | "reject" | "auto_approve" | null;
  decidedAt: string | null;
  doneByName: string | null;
  doneByEmail: string | null;
  isCurrent: boolean;
};

const DECISION_BADGE: Record<string, string> = {
  approve:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800",
  auto_approve:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800",
  reject:
    "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300 border border-rose-200 dark:border-rose-800",
  pending:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800",
  idle: "bg-gray-100 text-blue_grey2 dark:bg-gray-800 dark:text-gray-400 border border-gray-200 dark:border-gray-700",
};

function decisionLabel(d: StepView["decision"], isCurrent: boolean): string {
  if (d === "approve") return "approved";
  if (d === "auto_approve") return "auto-approved";
  if (d === "reject") return "rejected";
  return isCurrent ? "pending" : "waiting";
}

function decisionTone(d: StepView["decision"], isCurrent: boolean): string {
  if (d === "approve" || d === "auto_approve") return DECISION_BADGE.approve;
  if (d === "reject") return DECISION_BADGE.reject;
  return isCurrent ? DECISION_BADGE.pending : DECISION_BADGE.idle;
}

// Audit rows arrive newest-first (occurred_at DESC), so the first decision
// event matching a step order is its most-recent outcome.
function buildStepViews(approval: ApprovalState | undefined): StepView[] {
  if (!approval) return [];
  const decisionFor = (stepOrder: number) =>
    approval.audit.find(
      (e) =>
        e.stepOrder === stepOrder &&
        (e.eventType === "approval" ||
          e.eventType === "auto_approval" ||
          e.eventType === "rejection"),
    ) ?? null;
  return approval.assignments.map((a) => {
    const dec = decisionFor(a.stepOrder);
    return {
      key: a.assignmentId,
      stepOrder: a.stepOrder,
      stepName: a.stepName,
      decision: a.decision,
      decidedAt: a.decidedAt,
      doneByName: dec?.actorName ?? null,
      doneByEmail: dec?.actorEmail ?? null,
      isCurrent: approval.currentAssignment?.assignmentId === a.assignmentId,
    };
  });
}

const Avatar = ({
  text,
  seed,
  size = "md",
}: {
  text: string;
  seed: string;
  size?: "sm" | "md";
}) => {
  const dim = size === "sm" ? "w-8 h-8 text-caption" : "w-10 h-10 text-small";
  return (
    <span
      className={`shrink-0 ${dim} rounded-full flex items-center justify-center font-semibold ${toneFor(seed)}`}
    >
      {text}
    </span>
  );
};

const SectionHeader = ({
  icon,
  label,
  badge,
  badgeRight,
  editable = false,
}: {
  icon: React.ReactNode;
  label: string;
  badge?: string;
  badgeRight?: string;
  editable?: boolean;
}) => (
  <div className="flex items-center gap-2 mb-3">
    <span
      className={`flex items-center gap-2 ${
        editable ? "text-penn-700 dark:text-penn-400" : "text-blue_grey2"
      }`}
    >
      {icon}
      <PennText
        as="span"
        size="xs"
        font="semibold"
        color={editable ? "blue_grey1" : "blue_grey2"}
        className="uppercase"
      >
        {label}
      </PennText>
    </span>
    {badge && (
      <span
        className={`px-2 py-0.5 text-caption rounded border ${
          editable
            ? "bg-penn-100 text-penn-700 border-penn-400 dark:bg-penn-900/30 dark:text-penn-400 dark:border-penn-700"
            : "bg-gray-50 text-blue_grey3 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700"
        }`}
      >
        {badge}
      </span>
    )}
    {badgeRight && (
      <span className="ml-auto px-2 py-0.5 text-caption rounded border bg-gray-50 text-blue_grey3 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700">
        {badgeRight}
      </span>
    )}
  </div>
);

const MetaField = ({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) => (
  <div>
    <PennText as="p" size="xs" color="blue_grey2" className="mb-1">
      {label}
    </PennText>
    <div
      className={`text-body text-blue_grey break-words ${
        mono ? "font-mono" : ""
      }`}
    >
      {children}
    </div>
  </div>
);

const Chip = ({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "blue" | "teal" | "amber" | "purple" | "green";
}) => {
  const toneCls = {
    neutral:
      "bg-gray-100 text-blue_grey dark:bg-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700",
    blue: "bg-penn-100 text-penn-700 dark:bg-penn-900/30 dark:text-penn-400 border border-penn-400/40 dark:border-penn-700",
    teal: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800",
    amber:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800",
    purple:
      "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 border border-violet-200 dark:border-violet-800",
    green:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-800",
  }[tone];
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-small font-mono ${toneCls}`}
    >
      {children}
    </span>
  );
};

function categoryChip(opt: AdminTaskDetailSelectedOption) {
  if (opt.toolingType) {
    return <Chip tone="amber">Tooling · {opt.toolingType}</Chip>;
  }
  if (opt.category) {
    const label = opt.category.charAt(0).toUpperCase() + opt.category.slice(1);
    return <Chip tone="teal">{label}</Chip>;
  }
  return <Chip>{PLACEHOLDER}</Chip>;
}

function liCategoryChips(item: AdminTaskDetailManualLineItem) {
  const chips: React.ReactNode[] = [];
  if (item.isManual)
    chips.push(
      <Chip key="m" tone="purple">
        Manual
      </Chip>,
    );
  if (item.category) {
    const label =
      item.category.charAt(0).toUpperCase() + item.category.slice(1);
    chips.push(
      <Chip key="c" tone="teal">
        {label}
      </Chip>,
    );
  }
  return chips;
}

const Divider = () => (
  <div className="border-t border-gray-100 dark:border-gray-800" />
);

export const TaskEditModal = ({ taskId, onClose }: Props) => {
  const me = useAppSelector((s) => s.auth.user);
  // Per the workflow decision, approval decisions from THIS modal are admin-only;
  // `canManageUsers` is the admin signal (mirrors svc-07 decide()'s isAdmin).
  // Staff who can approve may still read the flow state (endpoint requires it).
  const isAdmin = !!me?.role?.canManageUsers;
  const canReadApproval = isAdmin || !!me?.role?.canApproveEngineering;

  const { data: task, isLoading: taskLoading } = useAdminTaskDetail(taskId);
  const { data: approval, isLoading: approvalLoading } = useApprovalState(
    taskId,
    canReadApproval && !!task?.snapshot,
  );

  const addCommentMutation = useAddTaskComment();
  const respondAndCloseMutation = useRespondAndCloseTask();
  const supportDocumentMutation = useDownloadSupportResponse();
  const decideMutation = useDecideApproval();
  const finalizeMutation = useFinalizeQuote();

  const [newComment, setNewComment] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [decisionComment, setDecisionComment] = useState("");
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);

  useEffect(() => {
    setNewComment("");
    setDecisionComment("");
    setApprovalError(null);
    setFinalizeError(null);
    setError(null);
    setDocumentError(null);
  }, [taskId]);

  const meName =
    [me?.firstName, me?.lastName].filter(Boolean).join(" ").trim() ||
    me?.email ||
    "You";

  const requesterName = useMemo(() => {
    if (!task) return PLACEHOLDER;
    const r = task.requestedBy;
    const full = [r.firstName, r.lastName].filter(Boolean).join(" ").trim();
    return full || r.email || PLACEHOLDER;
  }, [task]);

  const lineItemsTotal = useMemo(() => {
    if (!task || task.manualLineItems.length === 0) return null;
    let total = 0;
    let currency: string | null = null;
    for (const li of task.manualLineItems) {
      if (li.price === null) continue;
      const qty = li.qty ?? 1;
      total += li.price * qty;
      currency = currency ?? li.currency;
    }
    return { total, currency };
  }, [task]);

  const isMessageOnlySupport =
    !!task && task.snapshot === null && !!task.comment?.trim();
  // CR-005: a support response stamps the task Quoted (no "closed" status), so
  // "already responded" is derived from quotedAt.
  const isClosedSupport = isMessageOnlySupport && task.quotedAt != null;

  // Append a comment to the task thread (task_comments). `close` = true when
  // invoked from the footer Save (commit pending comment then close); false
  // from the inline "Post comment" (stay open so several can be added).
  const handlePostComment = (close: boolean) => {
    setError(null);
    const trimmed = newComment.trim();
    if (!trimmed) {
      if (close) onClose();
      else setError("Enter a comment before posting.");
      return;
    }
    addCommentMutation.mutate(
      { taskId, comment: trimmed },
      {
        onSuccess: () => {
          setNewComment("");
          if (close) onClose();
        },
        onError: (e) => setError(apiErrorMessage(e, "Could not post comment.")),
      },
    );
  };

  const handleRespondAndClose = () => {
    setError(null);
    const response = newComment.trim();
    if (!response) {
      setError("Enter a response before closing the support request.");
      return;
    }
    respondAndCloseMutation.mutate(
      { taskId, response },
      {
        onSuccess: () => setNewComment(""),
        onError: (e) =>
          setError(
            apiErrorMessage(e, "Could not respond and close the request."),
          ),
      },
    );
  };

  const handleDownloadSupportResponse = (format: "pdf" | "docx") => {
    setDocumentError(null);
    supportDocumentMutation.mutate(
      { taskId, format },
      {
        onError: (e) =>
          setDocumentError(
            apiErrorMessage(
              e,
              `Could not download the ${format.toUpperCase()} response.`,
            ),
          ),
      },
    );
  };

  const stepViews = useMemo(() => buildStepViews(approval), [approval]);
  const current = approval?.currentAssignment ?? null;
  const canDecide =
    isAdmin && !!current && !approval?.complete && !approval?.rejected;

  const handleDecide = (decision: "approve" | "reject") => {
    setApprovalError(null);
    decideMutation.mutate(
      { taskId, body: { decision, comment: decisionComment.trim() || null } },
      {
        onSuccess: () => setDecisionComment(""),
        onError: (e) =>
          setApprovalError(
            apiErrorMessage(e, "Could not record the decision."),
          ),
      },
    );
  };

  // Finalize is NOT automatic on approval completion (svc-06 keeps it an
  // explicit, approval-gated step). Admin finalizes here once the flow is
  // complete; svc-06 stamps the task Quoted (409 if approval is incomplete).
  const handleFinalize = () => {
    setFinalizeError(null);
    finalizeMutation.mutate(
      { taskId },
      {
        onError: (e) =>
          setFinalizeError(apiErrorMessage(e, "Could not finalize the quote.")),
      },
    );
  };

  return (
    <AdminModal
      open
      onClose={onClose}
      title="Edit task"
      maxWidth="max-w-3xl"
      footer={
        <>
          <CancelButton onClick={onClose} />
          {!isMessageOnlySupport && (
            <SaveButton
              onClick={() => handlePostComment(true)}
              label={addCommentMutation.isPending ? "Saving…" : "Save"}
            />
          )}
        </>
      }
    >
      {taskLoading || !task ? (
        <div className="py-12 text-center text-blue_grey2 text-body">
          Loading…
        </div>
      ) : (
        <div className="space-y-6">
          {/* TOP METADATA */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4 border-b border-gray-100 dark:border-gray-800">
            <MetaField label="ID" mono>
              {task.id || PLACEHOLDER}
            </MetaField>
            <MetaField label="Status">
              {task.quotedAt ? (
                <Chip tone="blue">quoted</Chip>
              ) : task.status ? (
                <Chip tone="blue">{task.status}</Chip>
              ) : (
                PLACEHOLDER
              )}
            </MetaField>
            <MetaField label="Created at" mono>
              {fmtUtc(task.createdAt)}
            </MetaField>
            <MetaField label="Updated at" mono>
              {fmtUtc(task.updatedAt)}
            </MetaField>
          </div>

          {/* REQUESTED BY */}
          <div>
            <SectionHeader
              icon={<User size={13} />}
              label="Requested by"
              badge="Read-only"
            />
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-4 py-3 space-y-3">
              <div className="flex items-center gap-3">
                <Avatar
                  text={initials(requesterName, task.requestedBy.email)}
                  seed={task.requestedBy.email ?? task.id}
                />
                <div className="min-w-0">
                  <div className="text-blue_grey truncate text-body-semibold">
                    {requesterName}
                  </div>
                  <div className="text-blue_grey2 truncate text-small">
                    {task.requestedBy.role || PLACEHOLDER}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-blue_grey text-small">
                <span className="inline-flex items-center gap-2">
                  <Mail size={13} className="text-blue_grey2" />
                  {task.requestedBy.email || PLACEHOLDER}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Phone size={13} className="text-blue_grey2" />
                  {task.requestedBy.phone || PLACEHOLDER}
                </span>
              </div>
              <div className="pt-3 border-t border-gray-200 dark:border-gray-800">
                <div className="text-blue_grey text-body-semibold">
                  {task.requestedBy.company.name || PLACEHOLDER}
                </div>
                <div className="text-blue_grey2 whitespace-pre-line leading-relaxed mt-1 text-small">
                  {task.requestedBy.company.address || PLACEHOLDER}
                </div>
              </div>
            </div>
          </div>

          {isMessageOnlySupport && (
            <div>
              <SectionHeader
                icon={<MessageSquare size={13} />}
                label="Support request"
                badge="Message-only"
              />
              <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-4 py-3 text-blue_grey whitespace-pre-wrap text-body">
                {task.comment}
              </div>
            </div>
          )}

          {/* COMMENTS */}
          <div>
            <SectionHeader
              icon={<MessageSquare size={13} />}
              label={isMessageOnlySupport ? "Responses" : "Comments"}
              badge={
                task.comments === null
                  ? "Not available"
                  : `${task.comments.length} ${task.comments.length === 1 ? "comment" : "comments"}`
              }
            />
            <div className="space-y-4">
              {/* Scrollable comment thread — caps height so the modal doesn't
                  grow unbounded; the add-comment row below stays in view. */}
              <div className="max-h-80 space-y-4 overflow-y-auto pr-1">
                {(task.comments === null || task.comments.length === 0) && (
                  <div className="flex items-start gap-3">
                    <Avatar text={PLACEHOLDER} seed={task.id} size="sm" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-blue_grey2 text-body-semibold">
                          {PLACEHOLDER}
                        </span>
                        <span className="text-blue_grey2 text-small">
                          {PLACEHOLDER}
                        </span>
                        <span className="ml-auto font-mono text-blue_grey2 text-small">
                          {PLACEHOLDER}
                        </span>
                      </div>
                      <div className="mt-1 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800 px-3 py-2 italic text-blue_grey2 text-body">
                        {task.comments === null
                          ? "Comments are not available yet."
                          : "No comments yet."}
                      </div>
                    </div>
                  </div>
                )}
                {(task.comments ?? []).map((c) => {
                  const author =
                    c.createdBy?.name ?? c.createdBy?.email ?? PLACEHOLDER;
                  const seed = c.createdBy?.email ?? c.createdBy?.name ?? c.id;
                  return (
                    <div key={c.id} className="flex items-start gap-3">
                      <Avatar
                        text={initials(author, c.createdBy?.email)}
                        seed={seed}
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-blue_grey truncate text-body-semibold">
                            {author}
                          </span>
                          {c.createdBy?.role && (
                            <span className="text-blue_grey2 text-small">
                              {c.createdBy.role}
                            </span>
                          )}
                          <span className="ml-auto font-mono text-blue_grey2 text-small">
                            {fmtUtcShort(c.createdAt)}
                          </span>
                        </div>
                        <div className="mt-1 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800 px-3 py-2 text-blue_grey whitespace-pre-wrap text-body">
                          {c.comment}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Editable current-user row */}
              {!isClosedSupport && (
                <div className="flex items-start gap-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                  <Avatar
                    text={initials(meName, me?.email)}
                    seed={me?.email ?? "you"}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <PennTextarea
                      rows={3}
                      maxLength={5000}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder={
                        isMessageOnlySupport
                          ? "Write the response that will be sent with this support request..."
                          : "Add a comment..."
                      }
                    />
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-small text-blue_grey2">
                        Will be posted as {meName} · just now
                      </span>
                      <div className="flex items-center gap-2">
                        {!isMessageOnlySupport && (
                          <PennButton
                            variant="primary"
                            size="sm"
                            onClick={() => handlePostComment(false)}
                            disabled={
                              newComment.trim().length === 0 ||
                              addCommentMutation.isPending
                            }
                          >
                            {addCommentMutation.isPending
                              ? "Posting…"
                              : "Post comment"}
                          </PennButton>
                        )}
                        {isMessageOnlySupport && (
                          <PennButton
                            variant="primary"
                            size="sm"
                            onClick={handleRespondAndClose}
                            disabled={
                              newComment.trim().length === 0 ||
                              respondAndCloseMutation.isPending
                            }
                          >
                            {respondAndCloseMutation.isPending
                              ? "Closing…"
                              : "Mark as Closed"}
                          </PennButton>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {isClosedSupport && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-800 dark:bg-emerald-900/20">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-emerald-800 dark:text-emerald-200 text-body-medium">
                      This support request has been responded to and marked Quoted.
                    </span>
                    <div className="flex items-center gap-2">
                      <PennButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDownloadSupportResponse("pdf")}
                        disabled={supportDocumentMutation.isPending}
                      >
                        Download PDF
                      </PennButton>
                      <PennButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDownloadSupportResponse("docx")}
                        disabled={supportDocumentMutation.isPending}
                      >
                        Download Word
                      </PennButton>
                    </div>
                  </div>
                  {documentError && (
                    <div className="mt-2 text-red-600 text-small">
                      {documentError}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {!isMessageOnlySupport && (
            <>
              <Divider />

              {/* SNAPSHOT */}
              <div>
                <SectionHeader
                  icon={<Camera size={13} />}
                  label="Snapshot"
                  badge="Read-only"
                />

                <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-4 py-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <MetaField label="Source" mono>
                      {task.snapshot?.source || PLACEHOLDER}
                    </MetaField>
                    <MetaField label="Created at (client)" mono>
                      {fmtUtc(task.snapshot?.createdAtClient)}
                    </MetaField>
                  </div>
                </div>

                <PennText
                  as="p"
                  size="xs"
                  color="blue_grey2"
                  className="mt-4 mb-2"
                >
                  Solution
                </PennText>
                <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-4 py-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <MetaField label="Product ID" mono>
                      {task.snapshot?.solution.productId || PLACEHOLDER}
                    </MetaField>
                    <MetaField label="Name">
                      {task.snapshot?.solution.solutionName || PLACEHOLDER}
                    </MetaField>
                    <MetaField label="Machine type" mono>
                      {task.snapshot?.solution.machineType || PLACEHOLDER}
                    </MetaField>
                  </div>
                </div>

                <PennText
                  as="p"
                  size="xs"
                  color="blue_grey2"
                  className="mt-4 mb-2"
                >
                  Selected parts
                </PennText>
                <div className="flex flex-wrap gap-2">
                  {task.snapshot &&
                  task.snapshot.selections.selectedPartNos.length > 0 ? (
                    task.snapshot.selections.selectedPartNos.map((p) => (
                      <Chip key={p}>{p}</Chip>
                    ))
                  ) : (
                    <Chip>{PLACEHOLDER}</Chip>
                  )}
                </div>

                <PennText
                  as="p"
                  size="xs"
                  color="blue_grey2"
                  className="mt-4 mb-2"
                >
                  Selected options
                </PennText>
                <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-2 overflow-x-auto">
                  <table className="w-full text-small">
                    <thead>
                      <tr className="text-blue_grey2 uppercase">
                        <th className="text-left font-medium px-2 py-2 border-b border-gray-200 dark:border-gray-800">
                          Part no
                        </th>
                        <th className="text-left font-medium px-2 py-2 border-b border-gray-200 dark:border-gray-800">
                          Description
                        </th>
                        <th className="text-left font-medium px-2 py-2 border-b border-gray-200 dark:border-gray-800">
                          Category
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {task.snapshot &&
                      task.snapshot.selections.selectedOptions.length > 0 ? (
                        task.snapshot.selections.selectedOptions.map(
                          (opt, idx, arr) => (
                            <tr
                              key={opt.partNo}
                              className={
                                idx < arr.length - 1
                                  ? "border-b border-gray-100 dark:border-gray-800"
                                  : ""
                              }
                            >
                              <td className="px-2 py-2 font-mono whitespace-nowrap text-blue_grey">
                                {opt.partNo}
                              </td>
                              <td className="px-2 py-2 text-blue_grey2">
                                {opt.description || PLACEHOLDER}
                              </td>
                              <td className="px-2 py-2">{categoryChip(opt)}</td>
                            </tr>
                          ),
                        )
                      ) : (
                        <tr>
                          <td className="px-2 py-2 font-mono text-blue_grey2">
                            {PLACEHOLDER}
                          </td>
                          <td className="px-2 py-2 text-blue_grey2">
                            {PLACEHOLDER}
                          </td>
                          <td className="px-2 py-2">
                            <Chip>{PLACEHOLDER}</Chip>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <PennText
                  as="p"
                  size="xs"
                  color="blue_grey2"
                  className="mt-4 mb-2"
                >
                  Applications
                </PennText>
                {task.snapshot && task.snapshot.applications.length > 0 ? (
                  task.snapshot.applications.map((app) => (
                    <div
                      key={app.id}
                      className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-4 py-3 space-y-3 mb-3"
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-mono text-blue_grey text-body-semibold">
                          {app.name || PLACEHOLDER}
                        </span>
                        {app.oem && <Chip tone="blue">{app.oem}</Chip>}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-small">
                        <div>
                          <div className="text-blue_grey2 text-label mb-1">
                            App number
                          </div>
                          <div className="text-blue_grey font-medium">
                            {app.applicationNumber || PLACEHOLDER}
                          </div>
                        </div>
                        <div>
                          <div className="text-blue_grey2 text-label mb-1">
                            Program
                          </div>
                          <div className="text-blue_grey font-medium">
                            {app.productionProgram || PLACEHOLDER}
                          </div>
                        </div>
                        <div>
                          <div className="text-blue_grey2 text-label mb-1">
                            Material
                          </div>
                          <div className="text-blue_grey font-medium">
                            {app.material || PLACEHOLDER}
                          </div>
                        </div>
                        <div>
                          <div className="text-blue_grey2 text-label mb-1">
                            Annual volume
                          </div>
                          <div className="text-blue_grey font-medium">
                            {formatNumberWithThousands(app.annualVolume)}
                          </div>
                        </div>
                        <div>
                          <div className="text-blue_grey2 text-label mb-1">
                            Batch size
                          </div>
                          <div className="text-blue_grey font-medium">
                            {formatNumberWithThousands(app.batchSize)}
                          </div>
                        </div>
                      </div>
                      <div className="pt-3 border-t border-gray-200 dark:border-gray-800">
                        <div className="text-label text-blue_grey2 uppercase mb-1">
                          Fasteners
                        </div>
                        {app.fasteners.length === 0 ? (
                          <div className="text-blue_grey2 text-small">
                            {PLACEHOLDER}
                          </div>
                        ) : (
                          <div className="space-y-1">
                            {app.fasteners.map((f, idx) => (
                              <div
                                key={`${app.id}-${idx}`}
                                className="flex items-center gap-3 text-small"
                              >
                                <span className="text-blue_grey2">
                                  {f.brand || PLACEHOLDER}
                                </span>
                                <span className="font-mono text-blue_grey">
                                  {f.type || PLACEHOLDER}
                                </span>
                                <span className="ml-auto text-blue_grey2">
                                  Qty / part (A-side):{" "}
                                  <strong className="text-blue_grey">
                                    {f.qtyPerPartA ?? PLACEHOLDER}
                                  </strong>
                                </span>
                                <span className="ml-auto text-blue_grey2">
                                  Qty / part (B-side):{" "}
                                  <strong className="text-blue_grey">
                                    {f.qtyPerPartB ?? PLACEHOLDER}
                                  </strong>
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-4 py-3 text-blue_grey2 text-small">
                    {PLACEHOLDER}
                  </div>
                )}

                <PennText
                  as="p"
                  size="xs"
                  color="blue_grey2"
                  className="mt-4 mb-2"
                >
                  Filters
                </PennText>
                <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-4 py-3 space-y-2">
                  <div className="flex flex-wrap gap-x-5 gap-y-2 items-center text-small">
                    <span className="inline-flex items-center gap-2">
                      <span className="text-blue_grey2">view</span>
                      <span className="font-mono font-medium text-blue_grey">
                        {task.snapshot?.filters.view || PLACEHOLDER}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <span className="text-blue_grey2">region</span>
                      <span className="font-mono font-medium text-blue_grey">
                        {task.snapshot?.filters.region || PLACEHOLDER}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <span className="text-blue_grey2">force</span>
                      <span className="font-mono font-medium text-blue_grey">
                        {task.snapshot?.filters.forceMinKN != null &&
                        task.snapshot?.filters.forceMaxKN != null
                          ? `${task.snapshot.filters.forceMinKN}–${task.snapshot.filters.forceMaxKN} kN`
                          : PLACEHOLDER}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <span className="text-blue_grey2">PM</span>
                      <span className="font-mono font-medium text-blue_grey">
                        {task.snapshot?.filters.pm == null
                          ? PLACEHOLDER
                          : task.snapshot.filters.pm
                            ? "on"
                            : "off"}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <span className="text-blue_grey2">RI</span>
                      <span className="font-mono font-medium text-blue_grey">
                        {task.snapshot?.filters.ri == null
                          ? PLACEHOLDER
                          : task.snapshot.filters.ri
                            ? "on"
                            : "off"}
                      </span>
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-small">
                    <span className="text-small text-blue_grey2">
                      Compared:
                    </span>
                    {task.snapshot &&
                    task.snapshot.filters.compareIds.length > 0 ? (
                      task.snapshot.filters.compareIds.map((id) => (
                        <Chip key={id}>{id}</Chip>
                      ))
                    ) : (
                      <Chip>{PLACEHOLDER}</Chip>
                    )}
                  </div>
                </div>
              </div>

              <Divider />

              {/* APPROVAL WORKFLOW — real svc-07 flow. Admins approve/reject the
              current step here; everyone with read access sees each step's
              outcome plus who decided it and when. */}
              <div>
                <SectionHeader
                  icon={<ListChecks size={13} />}
                  label="Approval workflow"
                  badge={
                    canDecide
                      ? "Action required"
                      : isAdmin
                        ? "Admin only"
                        : "Read-only"
                  }
                  editable={canDecide}
                />

                {!canReadApproval ? (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-4 py-3 text-blue_grey2 text-small">
                    You don't have permission to view the approval flow.
                  </div>
                ) : approvalLoading ? (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-4 py-3 text-blue_grey2 text-small">
                    Loading approval flow…
                  </div>
                ) : !approval || (stepViews.length === 0 && !current) ? (
                  <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/20 px-4 py-3 text-amber-700 dark:text-amber-300 text-small">
                    No approval flow has been started for this task yet, so
                    there is nothing to approve.
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-small-medium px-2 py-0.5 rounded ${
                          approval.rejected
                            ? DECISION_BADGE.reject
                            : approval.complete
                              ? DECISION_BADGE.approve
                              : current
                                ? DECISION_BADGE.pending
                                : DECISION_BADGE.idle
                        }`}
                      >
                        {approval.rejected
                          ? "Rejected"
                          : approval.complete
                            ? "Approved / complete"
                            : current
                              ? `Pending: ${current.stepName}`
                              : "Not started"}
                      </span>
                      <span className="text-label text-blue_grey2 uppercase">
                        {approval.flowType} flow
                      </span>
                    </div>

                    {stepViews.map((s) => {
                      const decided = s.decision != null;
                      return (
                        <div
                          key={s.key}
                          className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 space-y-3"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-blue_grey text-body-semibold">
                              {s.stepName}
                            </span>
                            <span
                              className={`text-small-medium px-2 py-0.5 rounded ${decisionTone(
                                s.decision,
                                s.isCurrent,
                              )}`}
                            >
                              {decisionLabel(s.decision, s.isCurrent)}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <MetaField label="Done at" mono>
                              {decided
                                ? fmtLocalShort(s.decidedAt)
                                : PLACEHOLDER}
                            </MetaField>
                            <MetaField label="Done by">
                              {decided ? (
                                <div className="space-y-1">
                                  <div className="font-medium text-blue_grey">
                                    {s.doneByName ??
                                      s.doneByEmail ??
                                      PLACEHOLDER}
                                  </div>
                                  {s.doneByName && s.doneByEmail && (
                                    <div className="font-mono text-blue_grey2 text-small">
                                      {s.doneByEmail}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                PLACEHOLDER
                              )}
                            </MetaField>
                          </div>
                        </div>
                      );
                    })}

                    {canDecide && current && (
                      <div className="rounded-lg border border-penn-400 dark:border-penn-700 bg-penn-50/40 dark:bg-penn-900/10 px-4 py-3 space-y-2">
                        <div className="flex flex-wrap gap-2">
                          <PennButton
                            variant="primary"
                            size="sm"
                            onClick={() => handleDecide("approve")}
                            disabled={decideMutation.isPending}
                          >
                            {decideMutation.isPending ? "Saving…" : "Approve"}
                          </PennButton>
                          <PennButton
                            variant="secondary"
                            size="sm"
                            onClick={() => handleDecide("reject")}
                            disabled={decideMutation.isPending}
                          >
                            Reject
                          </PennButton>
                        </div>
                        <PennText as="p" size="xs" color="blue_grey2">
                          Acting on step: {current.stepName}
                        </PennText>
                      </div>
                    )}

                    {isAdmin &&
                      !canDecide &&
                      (approval.complete || approval.rejected) && (
                        <PennText as="p" size="xs" color="blue_grey2">
                          This flow is{" "}
                          {approval.rejected ? "rejected" : "complete"} — no
                          further approval is required.
                        </PennText>
                      )}

                    {/* FINALIZE — approval completion is NOT auto-finalize. Once the
                    flow is approved/complete, an admin explicitly finalizes here;
                    svc-06 stamps the task Quoted (strict-POC: no customer email is
                    sent). 409 if the flow is still awaiting approval. */}
                    {isAdmin && approval.complete && (
                      <div className="rounded-lg border border-penn-400 dark:border-penn-700 bg-penn-50/40 dark:bg-penn-900/10 px-4 py-3 space-y-2">
                        {task.quotedAt ? (
                          <div className="flex items-center gap-2 text-green-700 dark:text-green-300 text-body">
                            <Chip tone="green">Quote finalized</Chip>
                            <span className="font-mono text-small">
                              {fmtLocalShort(task.quotedAt)}
                            </span>
                          </div>
                        ) : (
                          <>
                            <PennText as="p" size="xs" color="blue_grey2">
                              All approvals are complete. Finalize the quote to
                              mark this task Quoted.
                            </PennText>
                            <PennButton
                              variant="primary"
                              size="sm"
                              onClick={handleFinalize}
                              disabled={finalizeMutation.isPending}
                            >
                              {finalizeMutation.isPending
                                ? "Finalizing…"
                                : "Finalize quote"}
                            </PennButton>
                            {finalizeError && (
                              <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-3 py-2 text-red-700 dark:text-red-300 text-body">
                                {finalizeError}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {approvalError && (
                      <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-3 py-2 text-red-700 dark:text-red-300 text-body">
                        {approvalError}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Divider />

              {/* MANUAL LINE ITEMS */}
              <div>
                <SectionHeader
                  icon={<List size={13} />}
                  label="Manual line items"
                  badge="Read-only"
                  badgeRight={
                    lineItemsTotal && lineItemsTotal.total > 0
                      ? `${task.manualLineItems.length} item${task.manualLineItems.length === 1 ? "" : "s"} · ${formatPriceFromNumber(
                          lineItemsTotal.total,
                          lineItemsTotal.currency,
                        )}`
                      : `${task.manualLineItems.length} item${task.manualLineItems.length === 1 ? "" : "s"}`
                  }
                />

                <div className="space-y-3">
                  {task.manualLineItems.length === 0 && (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-4 py-3 text-blue_grey2 text-small">
                      {PLACEHOLDER}
                    </div>
                  )}

                  {task.manualLineItems.map((li) => {
                    const qty = li.qty ?? 1;
                    const unit = formatPriceFromNumber(li.price, li.currency);
                    const total =
                      li.price !== null
                        ? formatPriceFromNumber(li.price * qty, li.currency)
                        : PLACEHOLDER;
                    const seed =
                      li.createdBy?.name ?? li.createdBy?.email ?? li.id;
                    return (
                      <div
                        key={li.id}
                        className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-4 py-3 space-y-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0 space-y-1">
                            <div className="flex items-center gap-2 flex-wrap font-mono text-blue_grey text-body-semibold">
                              {li.partNo || PLACEHOLDER}
                              {liCategoryChips(li)}
                            </div>
                            <div className="text-blue_grey2 text-small">
                              {li.description || PLACEHOLDER}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-base font-mono font-semibold text-blue_grey">
                              {total}
                            </div>
                            <div className="text-small font-mono text-blue_grey2">
                              {qty} × {unit}
                            </div>
                          </div>
                        </div>
                        <div className="pt-3 border-t border-gray-200 dark:border-gray-800 flex items-center gap-3">
                          <Avatar
                            text={initials(
                              li.createdBy?.name,
                              li.createdBy?.email,
                            )}
                            seed={seed}
                            size="sm"
                          />
                          <div className="min-w-0">
                            <div className="text-blue_grey text-small">
                              Added by {li.createdBy?.name || PLACEHOLDER}
                              {li.createdBy?.role
                                ? ` · ${li.createdBy.role}`
                                : ""}
                            </div>
                            <div className="text-small font-mono text-blue_grey2">
                              {fmtUtc(li.createdAt)}
                              {li.createdBy?.email
                                ? ` · ${li.createdBy.email}`
                                : ""}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* QUOTE INFO */}
              {(task.quotedAt || task.quotedBy) && (
                <>
                  <Divider />
                  <div>
                    <SectionHeader
                      icon={<ListChecks size={13} />}
                      label="Quote information"
                      badge="Read-only"
                    />
                    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-4 py-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <MetaField label="Quoted at" mono>
                          {fmtUtc(task.quotedAt)}
                        </MetaField>
                        <MetaField label="Quoted by">
                          {task.quotedBy?.name ||
                            task.quotedBy?.email ||
                            PLACEHOLDER}
                        </MetaField>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-3 py-2 text-red-700 dark:text-red-300 text-body">
              {error}
            </div>
          )}
        </div>
      )}
    </AdminModal>
  );
};
